/**
 * IndexedDB Storage Manager for Offline Contracts
 * Handles local storage of contracts for offline access
 */

const DB_NAME = 'notaryos-offline';
const DB_VERSION = 1;

const STORES = {
  CONTRACTS: 'contracts',
  PENDING_CHANGES: 'pending_changes',
  SYNC_QUEUE: 'sync_queue',
  METADATA: 'metadata'
};

class OfflineStorageManager {
  constructor() {
    this.db = null;
    this.isReady = false;
    this.readyPromise = this.initDB();
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        console.log('IndexedDB initialized');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Contracts store - main offline storage
        if (!db.objectStoreNames.contains(STORES.CONTRACTS)) {
          const contractsStore = db.createObjectStore(STORES.CONTRACTS, { keyPath: 'id' });
          contractsStore.createIndex('owner_id', 'owner_id', { unique: false });
          contractsStore.createIndex('updated_at', 'updated_at', { unique: false });
          contractsStore.createIndex('sync_status', 'sync_status', { unique: false });
        }

        // Pending changes - edits made while offline
        if (!db.objectStoreNames.contains(STORES.PENDING_CHANGES)) {
          const changesStore = db.createObjectStore(STORES.PENDING_CHANGES, { 
            keyPath: 'change_id', 
            autoIncrement: true 
          });
          changesStore.createIndex('contract_id', 'contract_id', { unique: false });
          changesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Sync queue - operations to perform when online
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { 
            keyPath: 'queue_id', 
            autoIncrement: true 
          });
          syncStore.createIndex('operation', 'operation', { unique: false });
          syncStore.createIndex('priority', 'priority', { unique: false });
        }

        // Metadata - sync timestamps, user info, etc.
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          db.createObjectStore(STORES.METADATA, { keyPath: 'key' });
        }
      };
    });
  }

  async ensureReady() {
    if (!this.isReady) {
      await this.readyPromise;
    }
  }

  // ==================== CONTRACT OPERATIONS ====================

  async saveContract(contract) {
    await this.ensureReady();
    
    const contractWithMeta = {
      ...contract,
      _offline_saved_at: new Date().toISOString(),
      sync_status: 'synced'
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CONTRACTS], 'readwrite');
      const store = transaction.objectStore(STORES.CONTRACTS);
      const request = store.put(contractWithMeta);

      request.onsuccess = () => resolve(contractWithMeta);
      request.onerror = () => reject(request.error);
    });
  }

  async saveContracts(contracts) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CONTRACTS], 'readwrite');
      const store = transaction.objectStore(STORES.CONTRACTS);
      
      let savedCount = 0;
      contracts.forEach(contract => {
        const contractWithMeta = {
          ...contract,
          _offline_saved_at: new Date().toISOString(),
          sync_status: 'synced'
        };
        const request = store.put(contractWithMeta);
        request.onsuccess = () => savedCount++;
      });

      transaction.oncomplete = () => resolve(savedCount);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getContract(id) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CONTRACTS], 'readonly');
      const store = transaction.objectStore(STORES.CONTRACTS);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllContracts() {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CONTRACTS], 'readonly');
      const store = transaction.objectStore(STORES.CONTRACTS);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteContract(id) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CONTRACTS], 'readwrite');
      const store = transaction.objectStore(STORES.CONTRACTS);
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== PENDING CHANGES ====================

  async savePendingChange(contractId, changeType, changeData) {
    await this.ensureReady();
    
    const change = {
      contract_id: contractId,
      type: changeType, // 'edit', 'sign', 'update_status'
      data: changeData,
      timestamp: new Date().toISOString(),
      synced: false
    };

    // Also mark the contract as having pending changes
    const contract = await this.getContract(contractId);
    if (contract) {
      contract.sync_status = 'pending';
      contract._has_pending_changes = true;
      await this.saveContract(contract);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_CHANGES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      const request = store.add(change);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingChanges(contractId = null) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_CHANGES], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      
      let request;
      if (contractId) {
        const index = store.index('contract_id');
        request = index.getAll(contractId);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async clearPendingChanges(contractId) {
    await this.ensureReady();
    
    const changes = await this.getPendingChanges(contractId);
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_CHANGES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      
      changes.forEach(change => {
        store.delete(change.change_id);
      });

      transaction.oncomplete = () => resolve(changes.length);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async markChangeAsSynced(changeId) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_CHANGES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      const request = store.delete(changeId);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== SYNC QUEUE ====================

  async addToSyncQueue(operation, data, priority = 1) {
    await this.ensureReady();
    
    const queueItem = {
      operation, // 'save_contract', 'update_contract', 'sync_changes'
      data,
      priority,
      created_at: new Date().toISOString(),
      attempts: 0,
      last_attempt: null
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.add(queueItem);

      request.onsuccess = () => {
        // Register for background sync if available
        if ('serviceWorker' in navigator && 'sync' in window.registration) {
          navigator.serviceWorker.ready.then(reg => {
            reg.sync.register('sync-contracts');
          });
        }
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueue() {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result || [];
        // Sort by priority (higher first) then by created_at
        items.sort((a, b) => {
          if (a.priority !== b.priority) return b.priority - a.priority;
          return new Date(a.created_at) - new Date(b.created_at);
        });
        resolve(items);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async removeFromSyncQueue(queueId) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.delete(queueId);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSyncQueueItem(queueId, updates) {
    await this.ensureReady();
    
    return new Promise(async (resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const getRequest = store.get(queueId);

      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (item) {
          const updated = { ...item, ...updates };
          const putRequest = store.put(updated);
          putRequest.onsuccess = () => resolve(updated);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Item not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ==================== METADATA ====================

  async setMetadata(key, value) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.METADATA], 'readwrite');
      const store = transaction.objectStore(STORES.METADATA);
      const request = store.put({ key, value, updated_at: new Date().toISOString() });

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getMetadata(key) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.METADATA], 'readonly');
      const store = transaction.objectStore(STORES.METADATA);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== UTILITY ====================

  async getStorageStats() {
    await this.ensureReady();
    
    const contracts = await this.getAllContracts();
    const pendingChanges = await this.getPendingChanges();
    const syncQueue = await this.getSyncQueue();
    const lastSync = await this.getMetadata('last_sync');

    return {
      contracts_cached: contracts.length,
      pending_changes: pendingChanges.length,
      sync_queue_items: syncQueue.length,
      last_sync: lastSync,
      contracts_with_changes: contracts.filter(c => c.sync_status === 'pending').length
    };
  }

  async clearAllData() {
    await this.ensureReady();
    
    const stores = [STORES.CONTRACTS, STORES.PENDING_CHANGES, STORES.SYNC_QUEUE, STORES.METADATA];
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(stores, 'readwrite');
      
      stores.forEach(storeName => {
        transaction.objectStore(storeName).clear();
      });

      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageManager();
export default offlineStorage;
